import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import useCasEnabledDebouncedCallback from 'src/compass/shared/hooks/useCasEnabledDebouncedCallback';

export type ICasTextField = Omit<TextFieldProps, 'onChange'> & {
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: any) => void;
  placeholder?: string;
  name: string;
  label?: string;
  startAdornment?: ReactNode;
  className?: string;
  /*
    Delay the parent's onChange until typing pauses. `true` uses the default interval;
    a number sets it, for a filter whose endpoint is expensive enough to want a longer
    pause than the rest of the app.
  */
  debounce?: boolean | number;
  /*
    Rewrite what the user typed before it reaches the input or the parent - used to
    refuse characters the consumer of the value cannot handle (e.g. the Customers
    name search, whose full-text terms break on `&`). Applied per keystroke, so it
    must be cheap and idempotent.
  */
  sanitize?: (value: string) => string;
};

const CasTextField = ({
  label,
  className,
  onChange,
  placeholder,
  name,
  startAdornment,
  debounce = false,
  sanitize,
  value: parmValue,
  ...other
}: ICasTextField) => {
  /*
    Mirror a caller-supplied value locally so the input can be driven from outside
    (e.g. the CasTable filter drawer staging a draft, or a filter being cleared)
    while typing still feels instant when `debounce` delays the parent update.

    Only takes control when the caller actually passes `value` — callers using the
    uncontrolled `defaultValue` form keep working unchanged.
  */
  const isControlled = parmValue !== undefined;
  const [innerValue, setInnerValue] = useState(parmValue ?? '');

  useEffect(() => {
    if (isControlled) setInnerValue(parmValue ?? '');
  }, [parmValue, isControlled]);

  const debouncedOnChange = useCasEnabledDebouncedCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event, event.target.value);
    },
    !!debounce,
    typeof debounce === 'number' ? debounce : undefined
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    /*
      Rejected characters are removed from the DOM node itself, not just from the
      value we hand on. Dropping them only in state would leave the keystroke on
      screen: the sanitized result usually equals the previous state, so React
      re-renders nothing and the input keeps showing the character. Writing
      `target.value` moves the caret to the end, so put it back where the typing
      was - otherwise editing mid-string jumps to the end on every rejected key.
    */
    if (sanitize) {
      const el = event.target;
      const clean = sanitize(el.value);
      if (clean !== el.value) {
        const caret = (el.selectionStart ?? el.value.length) - (el.value.length - clean.length);
        el.value = clean;
        el.setSelectionRange(caret, caret);
      }
    }

    setInnerValue(event.target.value); // instant UI
    debouncedOnChange(event); // parent update, debounced when enabled
  };

  return (
    <TextField
      label={label}
      className={className}
      name={name}
      onChange={handleChange}
      InputProps={{
        startAdornment,
      }}
      {...(isControlled ? { value: innerValue } : {})}
      {...other}
    />
  );
};
export default CasTextField;
